import React, { useEffect } from 'react';
import { useGameStore } from '../store/gameStore';
import { useProgressionStore } from '../store/progressionStore';
import { BarChart, Activity, Target, Clock, List } from 'lucide-react';

export const DebriefModal: React.FC = () => {
  const { stats, gameStatus, reset, sectors, currentScenario } = useGameStore();
  const { currentPrimitive, updateScore, incrementLevel2Completion } = useProgressionStore();
  
  const blueSectors = sectors.filter(s => s.owner === 'Blue').length;
  const redSectors = sectors.filter(s => s.owner === 'Red').length;
  const victory = blueSectors > redSectors;

  const roi = stats.totalApSpent > 0 ? (stats.totalTrustGained / stats.totalApSpent).toFixed(2) : '0';
  const successRate = stats.actionsTaken > 0 ? ((stats.successfulActions / stats.actionsTaken) * 100).toFixed(0) : '0';
  
  const processedRef = React.useRef(false);

  useEffect(() => {
    if (gameStatus === 'debrief' && victory && !processedRef.current) {
      processedRef.current = true;
      
      // Level 1: Update Primitive Score
      if (currentPrimitive) {
        // Simple scoring: Base 70 + (Success Rate * 0.3)
        // If victory, guaranteed pass (>85) if performance was decent
        const performanceScore = Math.min(100, 70 + (parseInt(successRate) * 0.3));
        updateScore(currentPrimitive, Math.round(performanceScore));
      }
      
      // Level 2: Increment Completion
      if (currentScenario?.id.startsWith('combined-arms')) {
        incrementLevel2Completion();
      }
    } else if (gameStatus !== 'debrief') {
      processedRef.current = false;
    }
  }, [gameStatus, victory, currentPrimitive, currentScenario, successRate, updateScore, incrementLevel2Completion]);

  if (gameStatus !== 'debrief') return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md">
      <div className="bg-gray-900 border border-gray-600 p-8 rounded-2xl max-w-4xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        <h1 className="text-3xl font-bold text-white mb-2 tracking-widest border-b border-gray-700 pb-4 flex justify-between items-center">
          <span>MISSION DEBRIEF</span>
          <span className={victory ? "text-green-400" : "text-red-400"}>
            {victory ? "SUCCESS" : "FAILURE"}
          </span>
        </h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 mt-6">
          <div className="bg-gray-800 p-4 rounded-lg text-center">
            <Activity className="w-6 h-6 text-blue-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-white">{stats.totalApSpent}</div>
            <div className="text-xs text-gray-400 uppercase">AP Spent</div>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg text-center">
            <Target className="w-6 h-6 text-green-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-white">{stats.totalTrustGained}</div>
            <div className="text-xs text-gray-400 uppercase">Trust Gained</div>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg text-center">
            <BarChart className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-white">{roi}</div>
            <div className="text-xs text-gray-400 uppercase">Trust / AP (ROI)</div>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg text-center">
            <Clock className="w-6 h-6 text-purple-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-white">{successRate}%</div>
            <div className="text-xs text-gray-400 uppercase">Success Rate</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-gray-800/50 p-4 rounded-lg">
            <h3 className="text-white font-bold mb-4 text-sm uppercase">Sector Performance (ROI)</h3>
            <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
              {Object.entries(stats.roiBySector).map(([id, gain]) => {
                const sector = sectors.find(s => s.id === id);
                return (
                  <div key={id} className="flex justify-between text-sm border-b border-gray-700 pb-1">
                    <span className="text-gray-300">{sector?.name}</span>
                    <span className="text-green-400">+{gain} Trust</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-gray-800/50 p-4 rounded-lg">
            <h3 className="text-white font-bold mb-4 text-sm uppercase flex items-center gap-2">
              <List className="w-4 h-4" /> Decision Timeline
            </h3>
            <div className="space-y-2 max-h-60 overflow-y-auto text-xs font-mono">
              {stats.timeline.map((entry, idx) => (
                <div key={idx} className="flex gap-2 border-b border-gray-700/50 pb-1">
                  <span className="text-gray-500">T{entry.turn}</span>
                  <span className="text-blue-300 w-16">{entry.role}</span>
                  <span className="text-white flex-1">{entry.action}</span>
                  <span className={entry.result === 'Success' ? "text-green-400" : "text-red-400"}>
                    {entry.result}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="flex justify-end">
          <button 
            onClick={reset}
            className="px-6 py-3 bg-ops-blue hover:bg-blue-600 text-white font-bold rounded-lg transition-colors"
          >
            Return to Base
          </button>
        </div>
      </div>
    </div>
  );
};
